/**
 * `paramsDef` normalisation for the API editor.
 *
 * The field has three shapes in the wild:
 *   • `{key, value}`        — this editor's own "Query Params" rows
 *   • `{name, in, type, …}` — the canonical shape written by the MCP / opcode side
 *   • `"name"`              — bare strings
 *
 * `redisApiSchema.js` types it as a plain `Joi.array()` with no item schema, so
 * all three persist happily. The editor only ever read `{key}`, which meant a
 * record written by anything else threw on `param.key.trim()` while the dialog
 * was still opening — and with no error boundary in the app that blanks the
 * whole page rather than just the dialog.
 */

/**
 * Stored rows -> editor rows. Coerces everything to `{key, value, in}` for
 * display but KEEPS `name`: the opcode-side readers (`normalizeApiDefs`,
 * `normalizeParamsDef`) prefer `name`, so it stays authoritative and is
 * re-synced from `key` on save.
 */
export function normalizeParamsDefForEditor(paramsDef) {
    return (Array.isArray(paramsDef) ? paramsDef : [])
        .filter((p) => p !== null && p !== undefined)
        .map((p) => {
            if (typeof p === 'string') {
                return {
                    name: p,
                    key: p,
                    value: '',
                    in: 'query'
                };
            }
            return {
                ...p,
                key: String(p.key ?? p.name ?? ''),
                value: p.value ?? p.default ?? '',
                in: p.in || 'query'
            };
        });
}

/**
 * Editor rows -> stored rows. `key` is the field the user edits; a row that
 * arrived carrying a `name` gets it rewritten from `key`, otherwise renaming a
 * param here would leave every opcode pointing at the old name. Rows with no
 * key are dropped — an unnamed param can never be sent.
 */
export function serializeParamsDef(paramsDef) {
    return (Array.isArray(paramsDef) ? paramsDef : [])
        .map((p) => {
            const key = String(p?.key ?? p?.name ?? '').trim();
            if (!key) return null;
            const row = {
                ...p,
                key
            };
            if ('name' in row) row.name = key;
            return row;
        })
        .filter(Boolean);
}

/** Duplicate-key detection that cannot throw on a malformed row. */
export function paramKeyList(params) {
    return (Array.isArray(params) ? params : [])
        .map((param) => String(param?.key ?? param?.name ?? '').trim())
        .filter((key) => key !== '');
}
